import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import {
  FaCloudUploadAlt,
  FaFilePdf,
  FaSpinner,
  FaArrowLeft,
  FaUpload,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const AdminUploadPYQ = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    subjectCode: "",
    branch: "",
    semester: "",
    examYear: "",
    examType: "External",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  React.useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  React.useEffect(() => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const examTypes = ["Internal", "External", "CIE", "SEE"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) =>
    (currentYear - i).toString(),
  );

  const getBgColor = () => (isDarkMode ? "rgba(30, 30, 45, 0.95)" : "white");
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const onDrop = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile.size > 20 * 1024 * 1024) {
      toast.error("File size should be less than 20MB");
      return;
    }
    if (uploadedFile.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    setFile(uploadedFile);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a PDF file");
      return;
    }
    if (
      !formData.title ||
      !formData.subject ||
      !formData.subjectCode ||
      !formData.branch ||
      !formData.semester ||
      !formData.examYear
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setUploading(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    data.append("file", file);

    try {
      const token = localStorage.getItem("token");
      await axios.post("/api/pyqs", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("PYQ uploaded successfully!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to upload PYQ");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "800px",
        margin: "0 auto",
        padding: "2rem",
        minHeight: "calc(100vh - 100px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
      }}
    >
      <button
        onClick={() => navigate("/admin")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          background: "none",
          border: "none",
          color: "#8b5cf6",
          cursor: "pointer",
          marginBottom: "1.5rem",
          fontSize: "0.9rem",
        }}
      >
        <FaArrowLeft /> Back to Admin Dashboard
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: getBgColor(),
          borderRadius: "24px",
          padding: "2rem",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        <h1
          style={{
            color: getTextColor(),
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <FaFilePdf color="#ef4444" /> Upload Previous Year Question Paper
        </h1>
        <p
          style={{
            color: getTextColor(),
            opacity: 0.7,
            marginBottom: "1.5rem",
          }}
        >
          Add new question papers for students to access
        </p>

        <form onSubmit={handleSubmit}>
          {/* File Dropzone */}
          <div
            {...getRootProps()}
            style={{
              border: `2px dashed ${isDragActive ? "#8b5cf6" : getBorderColor()}`,
              borderRadius: "16px",
              padding: "2rem",
              textAlign: "center",
              marginBottom: "1.5rem",
              cursor: "pointer",
              background: isDragActive
                ? isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)"
                : "transparent",
            }}
          >
            <input {...getInputProps()} />
            {file ? (
              <div>
                <FaFilePdf size={60} color="#ef4444" />
                <p
                  style={{
                    color: getTextColor(),
                    marginTop: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  {file.name}
                </p>
                <p
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    opacity: 0.7,
                  }}
                >
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  style={{
                    marginTop: "0.5rem",
                    padding: "0.3rem 1rem",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "20px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <FaCloudUploadAlt size={60} color="#8b5cf6" />
                <p style={{ color: getTextColor(), marginTop: "0.5rem" }}>
                  {isDragActive
                    ? "Drop the PDF here"
                    : "Drag & drop a PDF, or click to select"}
                </p>
                <p
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    opacity: 0.7,
                  }}
                >
                  Only PDF files (Max 20MB)
                </p>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div style={{ display: "grid", gap: "1rem" }}>
            <div>
              <label style={{ color: getTextColor(), fontWeight: "500" }}>
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginTop: "0.3rem",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div>
              <label style={{ color: getTextColor(), fontWeight: "500" }}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  marginTop: "0.3rem",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                  resize: "vertical",
                }}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label style={{ color: getTextColor(), fontWeight: "500" }}>
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    marginTop: "0.3rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <div>
                <label style={{ color: getTextColor(), fontWeight: "500" }}>
                  Subject Code *
                </label>
                <input
                  type="text"
                  name="subjectCode"
                  value={formData.subjectCode}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    marginTop: "0.3rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                    textTransform: "uppercase",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label style={{ color: getTextColor(), fontWeight: "500" }}>
                  Branch *
                </label>
                <select
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    marginTop: "0.3rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {branches.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: getTextColor(), fontWeight: "500" }}>
                  Semester *
                </label>
                <select
                  name="semester"
                  value={formData.semester}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    marginTop: "0.3rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {semesters.map((s) => (
                    <option key={s} value={s}>
                      Semester {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: getTextColor(), fontWeight: "500" }}>
                  Exam Year *
                </label>
                <select
                  name="examYear"
                  value={formData.examYear}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    marginTop: "0.3rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: getTextColor(), fontWeight: "500" }}>
                  Exam Type
                </label>
                <select
                  name="examType"
                  value={formData.examType}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    marginTop: "0.3rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {examTypes.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={uploading}
            style={{
              width: "100%",
              padding: "1rem",
              marginTop: "1.5rem",
              background: uploading
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: uploading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            {uploading ? (
              <>
                <FaSpinner style={{ animation: "spin 1s linear infinite" }} />{" "}
                Uploading...
              </>
            ) : (
              <>
                <FaUpload /> Upload PYQ
              </>
            )}
          </button>
        </form>
      </motion.div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminUploadPYQ;

